import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    StyleSheet,
    StyleProp,
    TextStyle,
    ViewStyle,
    View,
    TextInput,
} from "react-native";
import { InputLabel } from "../input-label";
import { StyleFunction, useThemedStyle } from "../../theme";
import { Text } from "../text";
import { RequiredIndicator } from "../required-input";

export function NumberInput(props: {
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    defaultValue?: number | null;
    value?: number | null;
    onChange?: (value: number | null) => void;
    onInvalid?: () => void;
    style?: StyleProp<ViewStyle | TextStyle>;
    min?: number;
    max?: number;
    unit?: string;
    percentageToRate?: boolean;
    decimals?: number;
    testID?: string;
    required?: boolean;
    onSubmitEditing?: ({ nativeEvent: { text, eventCount, target } }) => void;
}) {
    const isControlled = props.value !== undefined;
    const inputDecimals = Math.max(0, props.decimals || 0);

    const formatCurrentValue = useCallback(
        (value: number | null | undefined) =>
            formatValue(value, {
                toPercentage: props.percentageToRate,
                decimals: inputDecimals,
            }),
        [props.percentageToRate, inputDecimals]
    );

    const [textValue, setTextValue] = useState<string>(
        formatCurrentValue(
            isControlled ? props.value : props.defaultValue ?? null
        )
    );
    const styles = useThemedStyle(styleFunc);

    // Allow leading minus( "-"), or minus and decimal separator "-," or "-.", and x decimals as defined in the arguments
    const inputRegex = useMemo(
        () =>
            new RegExp(`^-?$|^-?[,.]$|^-?\\d+([,.]\\d{0,${inputDecimals}})?$`),
        [inputDecimals]
    );

    // Sync external value → internal text in controlled mode only
    useEffect(() => {
        if (!isControlled) {
            return;
        }

        setTextValue(formatCurrentValue(props.value));
    }, [formatCurrentValue, isControlled, props.value]);

    const handleChangeText = useCallback(
        (text: string) => {
            if (!inputRegex.test(text)) {
                if (props.onInvalid) props.onInvalid();
                return;
            }

            setTextValue(text);
        },
        [inputRegex, props]
    );

    const commitValue = useCallback(
        (raw: string) => {
            if (raw.trim() === "") {
                props.onChange?.(null);
                return;
            }

            const normalized = raw.trim().replace(",", ".");
            const parsed = Number(normalized);
            if (Number.isNaN(parsed)) {
                setTextValue(
                    formatCurrentValue(
                        isControlled ? props.value : props.defaultValue
                    )
                );

                return;
            }

            let numericValue = parsed;

            numericValue = roundDecimals(numericValue, {
                decimals: inputDecimals,
            });

            if (props.percentageToRate) {
                numericValue = numericValue / 100;
            }

            numericValue = clampValue(numericValue, props);

            props.onChange?.(numericValue);

            const displayValue = props.percentageToRate
                ? numericValue * 100
                : numericValue;

            setTextValue(
                inputDecimals > 0
                    ? displayValue.toFixed(inputDecimals)
                    : String(displayValue)
            );
        },
        [inputDecimals, props, formatCurrentValue, isControlled]
    );

    const handleBlur = useCallback(() => {
        commitValue(textValue);
    }, [commitValue, textValue]);

    const handleSubmitEditing = useCallback(
        event => {
            commitValue(textValue);
            props.onSubmitEditing?.(event);
        },
        [commitValue, textValue, props]
    );

    return (
        <>
            {props.label && (
                <InputLabel>
                    {props.label} {props.required && <RequiredIndicator />}
                </InputLabel>
            )}
            <View style={styles.container}>
                {props.unit && (
                    <View style={styles.unit}>
                        <Text style={styles.unitText}>{props.unit}</Text>
                    </View>
                )}

                <TextInput
                    onChangeText={handleChangeText}
                    onBlur={handleBlur}
                    onSubmitEditing={handleSubmitEditing}
                    inputMode={
                        props.decimals && props.decimals > 0
                            ? "decimal"
                            : "numeric"
                    }
                    value={textValue}
                    style={StyleSheet.flatten([
                        styles.input,
                        props.disabled ? styles.disabled : undefined,
                        props.unit ? styles.unitInput : undefined,
                        props.style,
                    ])}
                    placeholder={props.placeholder}
                    editable={!props.disabled}
                    testID={props.testID}
                />
            </View>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        ...theme.styles.row,
    },

    input: {
        ...theme.styles.flex1,
        ...theme.styles.input,
        width: "100%",
        backgroundColor: theme.colors.white,
        paddingHorizontal: 8,

        borderStyle: "solid",
        borderRadius: 8,
        borderWidth: StyleSheet.hairlineWidth,
        borderColor: theme.colors.secondary,

        minHeight: theme.spacingScale * 4,
        marginBottom: theme.spacingScale,
    },

    disabled: {
        backgroundColor: theme.colors.grey50,
        borderColor: theme.colors.grey500,
        color: theme.colors.grey800,
    },

    unitInput: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderLeftWidth: 0,
    },

    unit: {
        backgroundColor: theme.colors.secondary,
        paddingHorizontal: 12,
        justifyContent: "center",

        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,

        minHeight: theme.spacingScale * 4,
        marginBottom: theme.spacingScale,
    },

    unitText: {
        ...theme.styles.smallText,
        ...theme.fonts.medium,
        color: theme.colors.white,
    },
});

function clampValue(value: number, props: { min?: number; max?: number }) {
    let val = value;

    if (props.min !== undefined) {
        val = Math.max(props.min, val);
    }
    if (props.max !== undefined) {
        val = Math.min(props.max, val);
    }

    return val;
}

export const percentageToRate = (value: number): number =>
    Math.round(value * 100) / 10000;

export const rateToPercentage = (value: number): number =>
    Math.round(value * 10000) / 100;

function roundDecimals(value: number, props: { decimals?: number }): number {
    if (props.decimals === undefined) {
        return value;
    }

    const sqrt = Math.pow(10, props.decimals);
    return Math.round(value * sqrt) / sqrt;
}

const formatValue = (
    value: number | null | undefined,
    props: { toPercentage?: boolean; decimals?: number }
) => {
    if (value === null || value === undefined) {
        return "";
    }

    const displayValue = props.toPercentage ? value * 100 : value;

    return props.decimals > 0
        ? displayValue.toFixed(props.decimals)
        : String(displayValue);
};
